import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ArrowDownCircle,
  ArrowUpCircle,
  BarChart3,
  RefreshCw,
  Gift,
} from "lucide-react";
import apiService from "../../apis/pinManageServices";
import toast from "react-hot-toast";

const CONFIG = {
  deposit: {
    title: "Total Deposit",
    subtitle: "Verified deposits list",
    icon: <ArrowDownCircle size={22} />,
    color: "text-blue-600",
    headers: ["#", "Sponsor ID", "User ID", "Amount", "Status", "Date"],
    row: (it, i) => [
      i + 1,
      it.sponsorID || "-",
      it.userID || "-",
      `$ ${Number(it.amount || 0).toFixed(2)}`,
      <span
        key="s"
        className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700"
      >
        {it.verficationStatus || "-"}
      </span>,
      formatDate(it.createdAt),
    ],
  },
  todayDeposit: {
    title: "Today Deposit",
    subtitle: "Deposits made today",
    icon: <ArrowDownCircle size={22} />,
    color: "text-cyan-600",
    headers: ["#", "Sponsor ID", "User ID", "Amount", "Status", "Date"],
    row: (it, i) => [
      i + 1,
      it.sponsorID || "-",
      it.userID || "-",
      `$ ${Number(it.amount || 0).toFixed(2)}`,
      <span
        key="s"
        className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700"
      >
        {it.verficationStatus || "-"}
      </span>,
      formatDate(it.createdAt),
    ],
  },
  withdrawalComplete: {
    title: "Total Withdrawal Complete",
    subtitle: "Completed withdrawal requests",
    icon: <ArrowUpCircle size={22} />,
    color: "text-green-600",
    headers: ["#", "Sponsor ID", "User ID", "Amount", "Wallet", "Date"],
    row: (it, i) => [
      i + 1,
      it.sponsorID || "-",
      it.userID || "-",
      `$ ${Number(it.amount || 0).toFixed(2)}`,
      it.walletType || "-",
      formatDate(it.createdAt),
    ],
  },
  pendingWithdrawal: {
    title: "Total Pending Withdrawal",
    subtitle: "Withdrawals awaiting approval",
    icon: <ArrowUpCircle size={22} />,
    color: "text-yellow-600",
    headers: ["#", "Sponsor ID", "User ID", "Amount", "Wallet", "Date"],
    row: (it, i) => [
      i + 1,
      it.sponsorID || "-",
      it.userID || "-",
      `$ ${Number(it.amount || 0).toFixed(2)}`,
      it.walletType || "-",
      formatDate(it.createdAt),
    ],
  },
  todayWithdrawalPending: {
    title: "Today Withdrawal Pending",
    subtitle: "Pending withdrawals requested today",
    icon: <ArrowUpCircle size={22} />,
    color: "text-orange-600",
    headers: ["#", "Sponsor ID", "User ID", "Amount", "Wallet", "Date"],
    row: (it, i) => [
      i + 1,
      it.sponsorID || "-",
      it.userID || "-",
      `$ ${Number(it.amount || 0).toFixed(2)}`,
      it.walletType || "-",
      formatDate(it.createdAt),
    ],
  },
  todayWithdrawalComplete: {
    title: "Today Withdrawal Complete",
    subtitle: "Completed withdrawals today",
    icon: <ArrowUpCircle size={22} />,
    color: "text-emerald-600",
    headers: ["#", "Sponsor ID", "User ID", "Amount", "Wallet", "Date"],
    row: (it, i) => [
      i + 1,
      it.sponsorID || "-",
      it.userID || "-",
      `$ ${Number(it.amount || 0).toFixed(2)}`,
      it.walletType || "-",
      formatDate(it.createdAt),
    ],
  },
  selfIncome: {
    title: "Total Self Income",
    subtitle: "Daily self income credits",
    icon: <BarChart3 size={22} />,
    color: "text-purple-600",
    headers: ["#", "User", "Sponsor ID", "Amount", "Level", "Date"],
    row: (it, i) => [
      i + 1,
      it.userId?.username || "-",
      it.userId?.sponsorID || "-",
      `$ ${Number(it.amount || 0)}`,
      it.level,
      formatDate(it.createdAt),
    ],
  },
  teamIncome: {
    title: "Team Level Income",
    subtitle: "Level income from downline",
    icon: <BarChart3 size={22} />,
    color: "text-indigo-600",
    headers: ["#", "User", "Sponsor ID", "From", "Amount", "Level", "Date"],
    row: (it, i) => [
      i + 1,
      it.userId?.username || "-",
      it.userId?.sponsorID || "-",
      it.fromUserId?.username || "-",
      `$ ${Number(it.amount || 0)}`,
      `Level ${it.level}`,
      formatDate(it.createdAt),
    ],
  },
  royalty: {
    title: "Total Royalty Income",
    subtitle: "Monthly royalty payouts",
    icon: <RefreshCw size={22} />,
    color: "text-amber-600",
    headers: [
      "#",
      "User",
      "Month",
      "Level",
      "Direct",
      "Team",
      "Royalty ($)",
      "Status",
    ],
    row: (it, i) => [
      i + 1,
      it.userId?.username || "-",
      it.month,
      `Level ${it.levelAchieved}`,
      `$ ${Number(it.directIncome || 0).toFixed(2)}`,
      `$ ${Number(it.levelIncome || 0).toFixed(2)}`,
      `$ ${Number(it.creditedAmount || 0).toFixed(2)}`,
      <span
        key="s"
        className={`px-2 py-0.5 rounded-full text-xs ${
          it.status === "paid"
            ? "bg-green-100 text-green-700"
            : "bg-yellow-100 text-yellow-700"
        }`}
      >
        {it.status}
      </span>,
    ],
  },
  reward: {
    title: "Total Reward Income",
    subtitle: "Level achievement rewards",
    icon: <Gift size={22} />,
    color: "text-pink-600",
    headers: ["#", "User", "Month", "Level", "Amount", "Status"],
    row: (it, i) => [
      i + 1,
      it.userId?.username || "-",
      it.month,
      `Level ${it.levelAchieved}`,
      `$ ${Number(it.amount || 0).toFixed(2)}`,
      <span
        key="s"
        className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700"
      >
        {it.status}
      </span>,
    ],
  },
};

function formatDate(dateString) {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const result = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) result.push("...");
    result.push(p);
    prev = p;
  }
  return result;
}

const AdminReportList = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const config = CONFIG[type];

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [apiResponse, setApiResponse] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAdminReportList(
        type,
        currentPage,
        limit
      );
      setApiResponse(response.data || {});
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to fetch report list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, currentPage, limit]);

  if (!config) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <h1 className="text-xl font-bold text-gray-800 mb-2">
            Invalid Report Type
          </h1>
          <button
            onClick={() => navigate("/admin/egw-10012026")}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { items = [], count = 0, pagination = {} } = apiResponse;
  const totalPages = Math.max(pagination.totalPages || 1, 1);
  const pageList = getPageList(currentPage, totalPages);
  const startIdx = (currentPage - 1) * limit + 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/egw-10012026")}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
            >
              <ArrowLeft size={18} />
              Back
            </button>
            <div>
              <div className="flex items-center gap-3">
                <span className={config.color}>{config.icon}</span>
                <h1 className="text-2xl font-bold text-gray-800">
                  {config.title}
                </h1>
              </div>
              <p className="text-gray-500 mt-1">{config.subtitle}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-3 border-b border-gray-100 text-sm text-gray-500">
            {loading ? (
              "Loading..."
            ) : count > 0 ? (
              <span>
                Showing{" "}
                <span className="font-semibold text-gray-700">{startIdx}</span>{" "}
                to{" "}
                <span className="font-semibold text-gray-700">
                  {Math.min(currentPage * limit, count)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-700">
                  {count.toLocaleString()}
                </span>{" "}
                records
              </span>
            ) : (
              "No records"
            )}
          </div>

          <div className="overflow-auto max-h-[55vh]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="border-b border-gray-200">
                  {config.headers.map((h, i) => (
                    <th
                      key={i}
                      className="text-left py-3 px-4 font-semibold text-gray-600 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={config.headers.length}
                      className="py-10 text-center text-gray-400"
                    >
                      {loading ? "Loading..." : "No records found"}
                    </td>
                  </tr>
                ) : (
                  items.map((it, idx) => (
                    <tr key={it._id || idx} className="hover:bg-gray-50">
                      {config.row(it, startIdx + idx).map((cell, i) => (
                        <td
                          key={i}
                          className="py-2.5 px-4 text-gray-700 whitespace-nowrap"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Show:</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  currentPage > 1 && setCurrentPage(currentPage - 1)
                }
                disabled={currentPage === 1}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} /> Prev
              </button>
              <div className="flex gap-1 items-center">
                {pageList.map((entry, i) =>
                  entry === "..." ? (
                    <span key={`dots-${i}`} className="px-1 text-gray-400">
                      ...
                    </span>
                  ) : (
                    <button
                      key={entry}
                      onClick={() => setCurrentPage(entry)}
                      className={`w-8 h-8 rounded-lg text-sm ${
                        currentPage === entry
                          ? "bg-blue-600 text-white"
                          : "border border-gray-300 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {entry}
                    </button>
                  )
                )}
              </div>
              <button
                onClick={() =>
                  currentPage < totalPages && setCurrentPage(currentPage + 1)
                }
                disabled={currentPage === totalPages || count === 0}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReportList;
